import React from 'react'
import managementService from '../../services/managementService'
import ManagementReportShell from './ManagementReportShell'
import useManagementReport from './useManagementReport'
import { KpiGrid, KpiTile, Section } from './ManagementWidgets'
import { ActionPanel, GapsTable, RiskPanel } from './ManagementGovernancePanels'
import { at, exportToSpreadsheet, flattenMetrics } from './managementUtils'

export const RiskSummaryTiles = ({ risks }) => (
  <KpiGrid>
    <KpiTile label="Total risks" metric={at(risks, 'summary.total')} />
    <KpiTile label="Open" metric={at(risks, 'summary.open')} />
    <KpiTile label="Red" metric={at(risks, 'summary.red')} tone="red" />
    <KpiTile label="Amber" metric={at(risks, 'summary.amber')} tone="amber" />
    <KpiTile label="Escalation required" metric={at(risks, 'summary.escalationRequired')} tone="red" />
  </KpiGrid>
)

export const ActionSummaryTiles = ({ actions }) => (
  <KpiGrid>
    <KpiTile label="Total actions" metric={at(actions, 'summary.total')} />
    <KpiTile label="Open" metric={at(actions, 'summary.open')} />
    <KpiTile label="Overdue" metric={at(actions, 'summary.overdue')} tone="red" />
    <KpiTile label="High priority open" metric={at(actions, 'summary.highPriorityOpen')} tone="amber" />
    <KpiTile label="Completed in period" metric={at(actions, 'summary.completedInPeriod')} tone="green" />
  </KpiGrid>
)

const pickExportFields = (list, fields) => (list || []).map((r) => Object.fromEntries(fields.map(([k, label, fn]) => [label, fn ? fn(r) : r[k]])))

const RiskComplianceActionTracker = () => {
  const report = useManagementReport(managementService.getRiskActions)
  const refreshSummary = () => report.reload({ silent: true })

  const onExport = (data) => exportToSpreadsheet('risk-compliance-actions', {
    Summary: flattenMetrics({ risks: data.risks?.summary, actions: data.actions?.summary, gapCount: data.gapCount }),
    Risks: pickExportFields(data.risks?.items, [
      ['title', 'Risk'], ['category', 'Category'], ['severity', 'Severity'], ['impact', 'Impact'], ['probability', 'Probability'],
      ['owner', 'Owner', (r) => r.owner?.name || ''], ['targetDate', 'Target date', (r) => (r.targetDate ? String(r.targetDate).slice(0, 10) : '')],
      ['status', 'Status'], ['daysOpen', 'Days open'], ['mitigation', 'Mitigation'], ['escalationRequired', 'Escalate', (r) => (r.escalationRequired ? 'Yes' : 'No')]
    ]),
    Actions: pickExportFields(data.actions?.items, [
      ['description', 'Action'], ['owner', 'Owner', (r) => r.owner?.name || ''], ['dueDate', 'Due', (r) => (r.dueDate ? String(r.dueDate).slice(0, 10) : '')],
      ['priority', 'Priority'], ['effectiveStatus', 'Status'], ['sourceReport', 'Source'], ['closureComment', 'Closure comment']
    ]),
    'Compliance gaps': (data.gaps || []).map((g) => ({ Check: g.label, Count: g.count, Status: g.severity, Module: g.sourceModule }))
  })

  return (
    <ManagementReportShell
      title="Risk, Compliance & Action Tracker"
      subtitle="Management risk register, action closure and automated data-compliance checks"
      report={report}
      onExport={onExport}
    >
      {(d) => (
        <>
          <Section title="Compliance gaps" subtitle={`${d.gapCount?.value ?? 0} checks need attention`}>
            <GapsTable gaps={d.gaps} />
          </Section>

          <Section title="Risk register">
            <RiskSummaryTiles risks={d.risks} />
            <div style={{ marginTop: 14 }}>
              <RiskPanel can={report.can} onChanged={refreshSummary} />
            </div>
          </Section>

          <Section title="Action tracker">
            <ActionSummaryTiles actions={d.actions} />
            <div style={{ marginTop: 14 }}>
              <ActionPanel can={report.can} onChanged={refreshSummary} sourceReport="risk-actions" />
            </div>
          </Section>
        </>
      )}
    </ManagementReportShell>
  )
}

export default RiskComplianceActionTracker
